import type { CreatedProduct } from '@/types/products'
import { isSuperseded } from '@/lib/productSupersession'

/**
 * The products the procedure label names beside "P": the procedure's main
 * products, i.e. not split aliquots (those are reached through their parent in
 * the Products dialog) and not ones superseded by a product-code change (their
 * replacement is listed instead). Oldest first, so the label does not reshuffle
 * as products are added.
 *
 * Procedure-wide on purpose: the label reads the same on every form of the
 * procedure. Pure so it is tested; see `procedureProductLabel.test.ts`.
 */
export function procedureMainProducts<
  T extends Pick<CreatedProduct, 'id' | 'parentProductId' | 'supersededById' | 'createdAt'>,
>(products: readonly T[]): T[] {
  const ids = new Set(products.map(p => p.id))
  return products
    .filter(p => !(p.parentProductId && ids.has(p.parentProductId)) && !isSuperseded(p))
    .sort((a, b) => a.createdAt.localeCompare(b.createdAt))
}

/**
 * The label beside "P": the first main product as "DIN / code", then "…" when
 * the procedure has more. The P count says how many; `title` names them all,
 * comma-separated, for the hover. Empty for no products.
 */
export function productLabel(
  products: readonly Pick<CreatedProduct, 'din' | 'productDescriptionCode'>[],
): { text: string; title: string } {
  const names = products.map(p => `${p.din} / ${p.productDescriptionCode}`)
  if (names.length === 0) return { text: '', title: '' }
  return { text: names.length > 1 ? `${names[0]} …` : names[0], title: names.join(', ') }
}
